import { Toast } from '@components/_atoms/Toast';
import { ApiResponseType, ErrorMessageCode } from '@constants/api';
import { LOGOUT } from '@constants/routes/routes';
import { HOUR, MINUTE, MONTH } from '@constants/time';

import { ACCESS_TOKEN, getCookie, LANGUAGE, REFRESH_TOKEN, removeCookie, setCookie } from '@utils/cookie';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getNewAuthTokenApi } from './auth';

const baseURL = process.env.NEXT_PUBLIC_API_URL;

const API = axios.create({
  baseURL,
  timeout: 3 * MINUTE * 1000, // ms 단위라 1000 곱해줌
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/* request */
API.interceptors.request.use(
  async function (config: AxiosRequestConfig) {
    if (config.url !== '/auth/login/' && config.url !== '/auth/register/') {
      const accessToken = getCookie(ACCESS_TOKEN);
      const Authorization = `Bearer ${accessToken}`;

      const language = getCookie(LANGUAGE);

      if (accessToken) {
        config.headers!.Authorization = Authorization;
      }
      if (language) {
        config.headers!['Accept-Language'] = language;
      }
    }

    return config;
  },

  function (error) {
    return Promise.reject(error);
  },
);

let retryCount = 0;
const maximumRetryCount = 3;
/* response */
API.interceptors.response.use(
  function (response: AxiosResponse<ApiResponseType>) {
    // If the request succeeds, we don't have to do anything and just return the response
    if (response && response.status === 200) {
      retryCount = 0;
    }

    return response;
  },
  async function (error: AxiosError<ApiResponseType>) {
    const errorResponse = error.response;
    const prevRequest = error.config;

    if (isExpiredAccessToken(errorResponse) || isAuthRequired(errorResponse)) {
      try {
        retryCount++;

        const currentDomain = window.location.hostname;

        const refreshToken = getCookie(REFRESH_TOKEN);
        if (refreshToken) {
          const refreshTokenData = await getNewAuthTokenApi({ refresh_token: refreshToken });

          const newAccessToken = refreshTokenData.data.data?.access_token;
          const newRefreshToken = refreshTokenData.data.data?.refresh_token;

          const currentDomain = window.location.hostname;
          setCookie(ACCESS_TOKEN, newAccessToken, { maxAge: 1 * HOUR, domain: currentDomain, path: '/' });
          setCookie(REFRESH_TOKEN, newRefreshToken, { maxAge: 3 * MONTH, domain: currentDomain, path: '/' });
          
          prevRequest.headers!.Authorization = newAccessToken!;

          return API(prevRequest);
        }
      } catch (refreshError) {
        const currentDomain = window.location.hostname;

        removeCookie(ACCESS_TOKEN, { domain: currentDomain, path: '/' });
        removeCookie(REFRESH_TOKEN, { domain: currentDomain, path: '/' });

        return Promise.reject(error);
      }
    }

    if (retryCount >= maximumRetryCount) {
      location.replace(LOGOUT);
    }

    if (isTimeoutError(error)) {
      Toast.error('요청시간이 만료되었습니다. 새로고침후 다시 시도해주세요.');
      // 위와 같이 처리하거나, errorBoundary로 error를 넘겨서 timeout이면 ErrorUI에 요청만료됨, 다시시도 해주세요. 와 같이 표시해주면될듯.
    }

    return Promise.reject(error);
  },
);

function isExpiredAccessToken(errorResponse: AxiosResponse<ApiResponseType, any> | undefined) {
  //errorResponse.status 가 400으로 오는 api들이 몇몇있어서 서버단에서 해결되면 errorResponse.status === 401 도 검사하는걸로 할예정입니다.
  if (errorResponse) {
    if (errorResponse.data.mcode === ErrorMessageCode.EXPIRED_TOKEN) {
      return true;
    }
    return false;
  }
  return false;
}

function isAuthRequired(errorResponse: AxiosResponse<ApiResponseType, any> | undefined) {
  if (errorResponse) {
    if (errorResponse.data.mcode === ErrorMessageCode.AUTH_REQUIRED && errorResponse?.status === 401) {
      return true;
    }
    return false;
  }
  return false;
}

function isTimeoutError(error: AxiosError<ApiResponseType>) {
  if (error.code === 'ECONNABORTED' || error.status === '408') {
    console.log(error.message);
    return true;
  }
  return false;
}

export default API;
